import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useDepots, fmtDepot } from "@/lib/depots";
import { Warning, Phone, MapPin, X, ArrowsClockwise, Buildings } from "@phosphor-icons/react";
import { Popup } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";

// Depot supervisor fallback contacts (used when no on-board crew is assigned)
const DEPOT_SUPERVISORS = {
  Panaji:   { name: "Panaji Depot Supervisor",   phone: "+91 91234 56700" },
  Margao:   { name: "Margao Depot Supervisor",   phone: "+91 91234 56701" },
  Vasco:    { name: "Vasco Depot Supervisor",    phone: "+91 91234 56702" },
  Mapusa:   { name: "Mapusa Depot Supervisor",   phone: "+91 91234 56703" },
  Ponda:    { name: "Ponda Depot Supervisor",    phone: "+91 91234 56704" },
  Canacona: { name: "Canacona Depot Supervisor", phone: "+91 91234 56705" },
};

// Shared content body — used by both desktop Leaflet Popup and mobile bottom sheet.
function BreakdownContent({ bus, inSheet = false }) {
  const depotMap = useDepots();
  const [crew, setCrew] = useState(null);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("nearest");
  const [loading, setLoading] = useState(false);
  const [expandedDepot, setExpandedDepot] = useState({});

  useEffect(() => {
    api.get(`/fleet/buses/${bus.fleet_id}/crew`).then(({data})=>setCrew(data)).catch(()=>{});
  }, [bus.fleet_id]);

  const findBackup = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/fleet/backup-suggestions/v2?broken_bus_id=${bus.id}&limit=5`);
      setData(data);
    } catch { toast.error("Failed to load backups"); }
    setLoading(false);
  };

  const dispatchBackup = async (s) => {
    try {
      await api.post("/dispatch/backup", { broken_bus_id: bus.id, backup_fleet_id: s.fleet_id });
      toast.success(`${s.fleet_id} dispatched · trip transferred · SMS sent`);
    } catch { toast.error("Dispatch failed"); }
  };

  return (
    <div className={inSheet ? "text-sm" : "text-xs"}>
      {!inSheet && (
        <div className="flex items-center gap-2 pb-2 mb-2 border-b border-red-200 sticky top-0 bg-white z-[1]">
          <div className="w-8 h-8 rounded-full bg-[#EF4444] flex items-center justify-center animate-pulse">
            <Warning size={16} className="text-white" weight="fill"/>
          </div>
          <div className="flex-1">
            <div className="font-mono font-black text-[#EF4444]">{bus.fleet_id} · BREAKDOWN</div>
            <div className="font-semibold text-slate-700">{bus.registration_number}</div>
          </div>
        </div>
      )}

      <div className={`grid grid-cols-2 gap-${inSheet ? "2" : "1.5"} mb-${inSheet ? "3" : "2"}`}>
        <div className="bg-slate-50 rounded p-2"><div className="text-[9px] uppercase text-slate-400">Depot</div><div className={`font-semibold ${inSheet ? "text-base" : ""}`}>{fmtDepot(bus.depot, depotMap)}</div></div>
        <div className="bg-slate-50 rounded p-2"><div className="text-[9px] uppercase text-slate-400">SOC</div><div className={`font-semibold ${inSheet ? "text-base" : ""}`}>{bus.soc}%</div></div>
      </div>

      {crew && (crew.driver || crew.conductor) && (
        <div className="space-y-2 mb-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">On-Board Crew · Tap to Call</div>
          {crew.driver && (
            <a href={`tel:${(crew.driver.phone || "").replace(/\s/g, "")}`} className="flex items-center gap-2 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 rounded-md p-2.5 transition-colors border border-blue-200">
              <div className={`${inSheet ? "w-9 h-9" : "w-8 h-8"} rounded-full bg-[#1E3A8A] flex items-center justify-center shrink-0`}><Phone size={inSheet ? 14 : 12} className="text-white" weight="fill"/></div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase text-slate-500 font-semibold">Driver</div>
                <div className={`font-bold text-[#1E3A8A] ${inSheet ? "text-sm" : ""} truncate`}>{crew.driver.name}</div>
              </div>
              <div className={`font-mono font-bold text-[#1E3A8A] ${inSheet ? "text-sm" : "text-xs"} shrink-0`}>{crew.driver.phone}</div>
            </a>
          )}
          {crew.conductor && (
            <a href={`tel:${(crew.conductor.phone || "").replace(/\s/g, "")}`} className="flex items-center gap-2 bg-green-50 hover:bg-green-100 active:bg-green-200 rounded-md p-2.5 transition-colors border border-green-200">
              <div className={`${inSheet ? "w-9 h-9" : "w-8 h-8"} rounded-full bg-[#22C55E] flex items-center justify-center shrink-0`}><Phone size={inSheet ? 14 : 12} className="text-white" weight="fill"/></div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase text-slate-500 font-semibold">Conductor</div>
                <div className={`font-bold text-[#22C55E] ${inSheet ? "text-sm" : ""} truncate`}>{crew.conductor.name}</div>
              </div>
              <div className={`font-mono font-bold text-[#22C55E] ${inSheet ? "text-sm" : "text-xs"} shrink-0`}>{crew.conductor.phone}</div>
            </a>
          )}
        </div>
      )}

      {crew && !crew.driver && !crew.conductor && (() => {
        const sup = DEPOT_SUPERVISORS[bus.depot] || { name: `${fmtDepot(bus.depot, depotMap)} Depot Supervisor`, phone: "+91 91234 56700" };
        return (
          <div className="space-y-2 mb-3">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold">No on-board crew · Tap to call supervisor</div>
            <a href={`tel:${sup.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 rounded-md p-2.5 transition-colors border border-amber-200">
              <div className={`${inSheet ? "w-9 h-9" : "w-8 h-8"} rounded-full bg-amber-500 flex items-center justify-center shrink-0`}><Buildings size={inSheet ? 14 : 12} className="text-white" weight="fill"/></div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase text-slate-500 font-semibold">{fmtDepot(bus.depot, depotMap)} Depot</div>
                <div className={`font-bold text-amber-700 ${inSheet ? "text-sm" : ""} truncate`}>{sup.name}</div>
              </div>
              <div className={`font-mono font-bold text-amber-700 ${inSheet ? "text-sm" : "text-[11px]"} shrink-0`}>{sup.phone}</div>
            </a>
          </div>
        );
      })()}

      {!data ? (
        <button onClick={findBackup} disabled={loading} className={`w-full bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-md ${inSheet ? "py-2.5 text-sm" : "py-1.5 text-xs"} font-semibold flex items-center justify-center gap-1.5`}>
          {loading ? <ArrowsClockwise size={inSheet ? 14 : 12} className="animate-spin"/> : <MapPin size={inSheet ? 14 : 12}/>}
          {loading ? "Finding…" : "Find Backup Bus Suggestions"}
        </button>
      ) : (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">{data.total_available} available across fleet</div>
          <div className={`flex gap-0.5 mb-1.5 ${inSheet ? "text-xs" : "text-[10px]"} border-b border-slate-200 overflow-x-auto scroll-thin`}>
            {[
              ["nearest", `Nearest (${data.nearest_overall.length})`],
              ["same_depot", `Same Depot (${data.same_depot.length})`],
              ["on_route", `On Route (${data.on_same_route.length})`],
              ["by_depot", `All Depots`],
            ].map(([k,l])=>(
              <button key={k} onClick={()=>setTab(k)} className={`shrink-0 ${inSheet ? "px-3 py-1.5" : "px-1.5 py-1"} whitespace-nowrap ${tab===k?"border-b-2 border-[#1E3A8A] text-[#1E3A8A] font-bold":"text-slate-500"}`}>{l}</button>
            ))}
          </div>
          <div className={`${inSheet ? "" : "max-h-44 overflow-auto scroll-thin"} space-y-1`}>
            {tab !== "by_depot" && (
              {nearest: data.nearest_overall, same_depot: data.same_depot, on_route: data.on_same_route}[tab]
            )?.map((s,i)=>(
              <div key={s.fleet_id} className={`flex items-center gap-2 rounded-md p-2 ${i===0?"bg-green-50 border border-green-200":"bg-slate-50"}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${i===0?"bg-[#22C55E] text-white":"bg-slate-300"}`}>{i+1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className={`font-mono font-bold text-[#1E3A8A] ${inSheet ? "text-xs" : "text-[11px]"}`}>{s.fleet_id}</span>
                    {s.same_depot && <span className="text-[8px] px-1 bg-blue-100 text-blue-700 rounded">SAME</span>}
                  </div>
                  <div className={`${inSheet ? "text-[11px]" : "text-[10px]"} text-slate-500`}>{fmtDepot(s.depot, depotMap)} · {s.distance_km}km · ETA {s.eta_min}m · 🔋{s.soc}%</div>
                </div>
                <button onClick={()=>dispatchBackup(s)} className={`ktcl-btn-primary ${inSheet ? "text-xs px-3 py-1.5" : "text-[10px] px-2 py-1"} rounded font-semibold shrink-0`}>Dispatch</button>
              </div>
            ))}
            {tab === "by_depot" && data.by_depot.map(d=>{
              const isExpanded = !!expandedDepot[d.depot];
              const visible = isExpanded ? d.buses : d.buses.slice(0, 3);
              return (
                <div key={d.depot} className="border border-slate-200 rounded-md overflow-hidden">
                  <div className="bg-slate-100 px-2 py-1.5 flex justify-between items-center text-[11px]">
                    <span className="font-bold text-slate-800">{fmtDepot(d.depot, depotMap)} Depot</span>
                    <span className="text-[10px] text-slate-600">{d.count} avail.</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {visible.map(s=>(
                      <div key={s.fleet_id} className="flex items-center gap-2 p-1.5">
                        <div className="flex-1 min-w-0">
                          <div className={`font-mono font-bold text-[#1E3A8A] ${inSheet ? "text-xs" : "text-[11px]"}`}>{s.fleet_id}</div>
                          <div className={`${inSheet ? "text-[11px]" : "text-[10px]"} text-slate-500`}>{s.bus_type} · {s.distance_km}km · 🔋{s.soc}%</div>
                        </div>
                        <button onClick={()=>dispatchBackup(s)} className={`ktcl-btn-primary ${inSheet ? "text-xs px-2.5 py-1" : "text-[10px] px-1.5 py-0.5"} rounded font-semibold shrink-0`}>Assign</button>
                      </div>
                    ))}
                    {d.count > 3 && (
                      <button data-testid={`depot-toggle-${d.depot}`}
                              onClick={()=>setExpandedDepot(x => ({...x, [d.depot]: !x[d.depot]}))}
                              className="w-full text-[10px] text-center py-1.5 text-[#1E3A8A] font-semibold bg-blue-50 hover:bg-blue-100 transition-colors">
                        {isExpanded ? `Show less ▲` : `+ ${d.count - 3} more buses ▼`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {tab === "on_route" && data.on_same_route.length === 0 && (
              <div className="text-[10px] italic text-slate-400 text-center py-3">
                {data.route_info ? `No other available bus running ${data.route_info.route_id}` : "Bus has no assigned route"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// Inline popup used on map markers — desktop only.
export function BreakdownPopup({ bus }) {
  return (
    <Popup minWidth={260} maxWidth={Math.min(340, (typeof window !== "undefined" ? window.innerWidth - 60 : 340))} autoPan={true}>
      <div
        ref={(el) => { if (el) { L.DomEvent.disableScrollPropagation(el); L.DomEvent.disableClickPropagation(el); } }}
        className="max-h-[60vh] overflow-y-auto overscroll-contain pr-1 scroll-thin"
      >
        <BreakdownContent bus={bus}/>
      </div>
    </Popup>
  );
}


// Bottom-sheet for mobile — proper native scroll, large tappable call buttons.
export function MobileBreakdownSheet({ bus, onClose }) {
  useEffect(() => {
    if (!bus) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [bus]);

  if (!bus) return null;
  return (
    <div className="fixed inset-0 z-[2000] lg:hidden">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}/>
      <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl max-h-[88dvh] shadow-2xl flex flex-col animate-slideUp">
        <div className="sticky top-0 bg-white rounded-t-2xl border-b border-red-200 px-4 pt-3 pb-3 flex items-center gap-2 z-10">
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1 bg-slate-300 rounded-full"/>
          <div className="w-9 h-9 rounded-full bg-[#EF4444] flex items-center justify-center animate-pulse shrink-0 mt-1">
            <Warning size={18} className="text-white" weight="fill"/>
          </div>
          <div className="flex-1 mt-1 min-w-0">
            <div className="font-mono font-black text-[#EF4444] text-sm">{bus.fleet_id} · BREAKDOWN</div>
            <div className="text-xs text-slate-600 truncate">{bus.registration_number}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 mt-1 shrink-0" aria-label="Close">
            <X size={20}/>
          </button>
        </div>
        <div className="px-4 py-3 overflow-y-auto overscroll-contain flex-1" style={{ WebkitOverflowScrolling: "touch" }}>
          <BreakdownContent bus={bus} inSheet={true}/>
          <div className="h-6"/>
        </div>
      </div>
    </div>
  );
}


// Floating panel that lists all breakdowns regardless of zoom level
export function BreakdownAlertPanel({ buses, onSelect }) {
  const depotMap = useDepots();
  const broken = buses.filter(b=>b.status==="Breakdown");
  const [open, setOpen] = useState(false);
  if (broken.length===0) return null;

  return (
    <div className={`absolute top-3 right-3 z-[450] bg-white rounded-lg shadow-lg border-2 border-[#EF4444] overflow-hidden transition-all ${open ? "w-[calc(100%-1.5rem)] max-w-[260px] sm:max-w-xs" : "w-auto"}`}>
      <div className="bg-[#EF4444] text-white px-2.5 sm:px-3 py-1.5 flex items-center justify-between cursor-pointer gap-2" onClick={()=>setOpen(!open)}>
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <Warning size={14} weight="fill" className="animate-pulse shrink-0"/>
          <span className="font-bold text-[11px] sm:text-xs truncate">{broken.length} BREAKDOWN{broken.length>1?"S":""}</span>
        </div>
        <span className="text-[10px] opacity-80 shrink-0">{open?"▴":"▾"}</span>
      </div>
      {open && (
        <div className="max-h-48 sm:max-h-56 overflow-auto scroll-thin">
          {broken.map(b=>(
            <button key={b.id} onClick={(e)=>{e.stopPropagation(); onSelect(b); setOpen(false);}} className="w-full text-left p-2 border-b border-slate-100 last:border-0 hover:bg-red-50 transition-colors">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#EF4444] animate-pulse shrink-0"/>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[11px] font-black text-[#EF4444]">{b.fleet_id}</div>
                  <div className="text-[10px] text-slate-600 truncate">{b.registration_number} · {fmtDepot(b.depot, depotMap)} · SOC {b.soc}%</div>
                </div>
                <Phone size={12} className="text-[#EF4444] shrink-0"/>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

